import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { coursesClient, tagsClient, workspacesClient } from '../apis/client-instances';
import { Course, Tag, TagType, UpdateWorkspaceAccessRequest, Workspace } from '../apis/api-client';
import { PageHeader } from '../components/common';
import { BookmarkIcon, DashboardIcon, GroupIcon, LibraryBooksIcon, PersonIcon, VisibilityIcon, WorkspacesIcon } from '../components/icons';
import { Box, Button, CircularProgress, Divider, Tab, Tabs, Typography } from '../components/ui';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { setAccount } from '../features/account/accountSlice';
import { alert, uploadFile, getFileUrl } from '../hooks/function';
import { impersonateWorkspaceAdmin } from '../utils/impersonation';
import { RootState } from '../types';
import { AccountType, accountTypeToTier, tierToAccountType } from '../utils/workspaceTier';
import UsersDirectory from '../components/users/UsersDirectory';
import AssignmentsDirectory from '../components/assignments/AssignmentsDirectory';
import GroupsDirectory from '../components/groups/GroupsDirectory';
import WorkspaceDashboardTab, { WidgetEditState } from '../components/workspaces/WorkspaceDashboardTab';
import WorkspaceAccessTab from '../components/workspaces/WorkspaceAccessTab';
import WorkspaceGeneralTab from '../components/workspaces/WorkspaceGeneralTab';
import WorkspaceDangerZone from '../components/workspaces/WorkspaceDangerZone';
import { DashboardWidget, parseWidgets, serializeWidgets } from '../utils/workspaceDashboardWidgets';

// "General" now covers what used to be two separate tabs (general +
// branding) — workspace name and brand identity belong on the same page
// since saving either requires the same backend round-trip anyway.
type WorkspaceTab = 'general' | 'access' | 'groups' | 'users' | 'assignments' | 'dashboard';

export default function WorkspaceEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { setLayoutConfig } = useLayoutConfig();
  const selectedWorkspace = useSelector((state: RootState) => state.workspaces.selectedWorkspace);
  const account = useSelector((state: RootState) => state.account);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  // Tab can be deep-linked via `?tab=<key>` so other surfaces can
  // jump straight to Assignments / Users / etc. (e.g., the Workspaces
  // list's Assign action). Defaults to 'general' when the param is
  // missing or unknown.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as WorkspaceTab | null;
  const validTabs: WorkspaceTab[] = ['general', 'access', 'groups', 'users', 'assignments', 'dashboard'];
  const initialTab: WorkspaceTab =
    tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : 'general';
  const [selectedTab, setSelectedTab] = useState<WorkspaceTab>(initialTab);
  // Keep URL in sync with the tab so refreshes/back-button preserve it.
  useEffect(() => {
    if (searchParams.get('tab') === selectedTab) return;
    const next = new URLSearchParams(searchParams);
    if (selectedTab === 'general') next.delete('tab');
    else next.set('tab', selectedTab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);
  // Reverse sync: when the URL's tab changes without going through the tab
  // control — browser back/forward, or a deep-link into an already-mounted
  // page — move the active tab to match. Without this the two drift apart.
  useEffect(() => {
    const t = searchParams.get('tab') as WorkspaceTab | null;
    const next: WorkspaceTab = t && validTabs.includes(t) ? t : 'general';
    if (next !== selectedTab) setSelectedTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Form state
  const [name, setName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [favicon, setFavicon] = useState('');
  const [bannerHtml, setBannerHtml] = useState('');
  const [bannerImageUrl, setBannerImageUrl] = useState('');
  // Local-state input for the "Image URL" field — separate from
  // `bannerImageUrl` (which holds the saved value) so an external URL
  // doesn't immediately overwrite an uploaded image on every keystroke.
  // Promoted into bannerImageUrl on blur or Apply.
  const [bannerImageUrlDraft, setBannerImageUrlDraft] = useState('');
  const [dashboardWidgetHtml, setDashboardWidgetHtml] = useState('');
  // Multi-widget editor state. Persisted into the single backend
  // `dashboardWidgetHtml` field via comment markers (see
  // utils/workspaceDashboardWidgets.ts). Legacy single-string content
  // auto-migrates into one untitled widget on load.
  const [dashboardWidgets, setDashboardWidgets] = useState<DashboardWidget[]>([]);
  // Per-widget UI-only edit state (which card is in edit mode, the
  // render-vs-code toggle, the revert snapshot). Owned here so the shared
  // save bar can serialize widgets; WorkspaceDashboardTab reads + mutates
  // it. See WidgetEditState for the shape.
  const [widgetEditStates, setWidgetEditStates] = useState<Record<string, WidgetEditState>>({});

  // File upload states
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [selectedFavicon, setSelectedFavicon] = useState<File | null>(null);
  const [selectedBannerImage, setSelectedBannerImage] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [bannerImagePreview, setBannerImagePreview] = useState<string | null>(null);

  // Danger-zone state — deactivation status (mirrors the workspace's
  // server-side `deactivatedAt`) and the delete confirmation modal. We
  // keep `deactivated` in state rather than reading on every render so
  // the toggle reflects immediately after a click without waiting for
  // the next data fetch.
  const [deactivated, setDeactivated] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Access tab — seat limit + tag-based + explicit-list content
  // gating. Reads off the loaded workspace; saves via the full-replace
  // `updateWorkspaceAccess` endpoint.
  const [seatLimitInput, setSeatLimitInput] = useState<string>('');
  const [contentTags, setContentTags] = useState<Tag[]>([]);
  const [provisionedContent, setProvisionedContent] = useState<Course[]>([]);
  const [accessSaving, setAccessSaving] = useState(false);
  // Account type — standard vs enterprise. Drives tier-gated features
  // (custom branding, SSO, advanced reporting). Default is 'standard'.
  const [accountType, setAccountType] = useState<AccountType>('standard');
  // Per-workspace self-service toggle. When true, learners on this
  // workspace can edit their own profile fields (name, email, photo,
  // professional role, licenses). Default false — most clients want
  // admin-managed rosters.
  const [allowLearnerSelfEdit, setAllowLearnerSelfEdit] = useState(false);
  // Per-workspace email notifications switch. Default ON — most
  // tenants want assignment / enrollment / deadline / invite emails.
  // Clients running their own HR or scheduling comms can flip this
  // off to keep the LMS silent without disabling the channel
  // tenant-wide.
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);

  // Live user total reported by UsersDirectory so the Users tab can
  // render a "Users (N)" header that matches the Groups tab's pattern.
  const [usersTotal, setUsersTotal] = useState(0);

  // Set layout config for back button. Hide the topbar's course-scoped
  // global search — this page is for managing the workspace itself,
  // which has nothing to do with the public course catalog. Reset on
  // unmount so the next page that does want the search gets it back.
  useEffect(() => {
    setLayoutConfig({
      showBackButton: true,
      backButtonText: 'Back to Workspaces',
      backButtonAction: () => navigate('/workspaces'),
      hideGlobalSearch: true,
    });
    return () => setLayoutConfig({ hideGlobalSearch: false });
  }, [setLayoutConfig, navigate]);

  // Fetch workspace data
  useEffect(() => {
    const fetchWorkspace = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const ws = await workspacesClient.getWorkspaceById(Number(id));
        setWorkspace(ws);
        setName(ws.name || '');
        setPrimaryColor(ws.primaryColor || '');
        setLogoUrl(ws.logoUrl || '');
        setFavicon(ws.favicon || '');
        setBannerHtml(ws.bannerHtml || '');
        setBannerImageUrl(ws.bannerImageUrl || '');
        setBannerImageUrlDraft(ws.bannerImageUrl || '');
        setDashboardWidgetHtml(ws.dashboardWidgetHtml || '');
        // Hydrate the multi-widget editor from the saved blob. Empty
        // → empty editor; legacy single-string content → one untitled
        // widget the admin can keep, rename, or split.
        setDashboardWidgets(parseWidgets(ws.dashboardWidgetHtml));
        // Activation status comes off the workspace itself — a non-null
        // `deactivatedAt` means it's currently deactivated.
        setDeactivated(ws.deactivatedAt != null);

        // Load preview URLs for existing images
        if (ws.logoUrl) {
          try {
            setLogoPreview(await getFileUrl(ws.logoUrl));
          } catch {
            setLogoPreview(null);
          }
        }
        if (ws.favicon) {
          try {
            setFaviconPreview(await getFileUrl(ws.favicon));
          } catch {
            setFaviconPreview(null);
          }
        }
        if (ws.bannerImageUrl) {
          try {
            setBannerImagePreview(await getFileUrl(ws.bannerImageUrl));
          } catch {
            setBannerImagePreview(null);
          }
        }
      } catch (error) {
        alert(enqueueSnackbar, 'Failed to load workspace', 'error');
        navigate('/workspaces');
      } finally {
        setLoading(false);
      }
    };
    fetchWorkspace();
  }, [id]);

  // Hydrate Access tab state from the loaded workspace + rehydrate
  // autocomplete chips by fetching the full Tag / Course objects for
  // the granted IDs (scalars ride the workspace; grant collections are
  // enriched on the single-workspace GET). Wrapped tolerantly so a
  // deleted tag/course doesn't crash the editor.
  useEffect(() => {
    if (!workspace) return;
    let cancelled = false;
    setSeatLimitInput(workspace.seatLimit != null ? String(workspace.seatLimit) : '');
    setAccountType(tierToAccountType(workspace.tier));
    setAllowLearnerSelfEdit(workspace.allowLearnerSelfEdit === true);
    setEmailNotificationsEnabled(workspace.emailNotificationsEnabled !== false);

    const contentTagIds = workspace.contentTagIds ?? [];
    const provisionedCourseIds = workspace.provisionedCourseIds ?? [];
    (async () => {
      try {
        const [tagsResult, coursesResult] = await Promise.all([
          contentTagIds.length > 0
            ? tagsClient
                .searchTags(
                  undefined,
                  TagType.CourseTag,
                  undefined,
                  true,
                  false,
                  contentTagIds,
                  1,
                  200,
                )
                .then((r) => r.items ?? [])
                .catch(() => [] as Tag[])
            : Promise.resolve([] as Tag[]),
          provisionedCourseIds.length > 0
            ? Promise.all(
                provisionedCourseIds.map((cid) =>
                  coursesClient.getCourseById(cid).catch(() => null),
                ),
              )
            : Promise.resolve([] as (Course | null)[]),
        ]);
        if (cancelled) return;
        setContentTags(tagsResult as Tag[]);
        setProvisionedContent(
          (coursesResult as (Course | null)[]).filter((c): c is Course => !!c),
        );
      } catch {
        if (!cancelled) {
          setContentTags([]);
          setProvisionedContent([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workspace]);

  const handleSave = async () => {
    if (!name?.trim()) {
      alert(enqueueSnackbar, 'Workspace name is required', 'error');
      return;
    }
    if (primaryColor && !/^#[0-9A-Fa-f]{6}$/.test(primaryColor)) {
      alert(enqueueSnackbar, 'Primary color must be a valid hex color (e.g., #3ea6d6)', 'error');
      return;
    }
    // Seat limit lives on the General tab alongside the workspace name
    // since it shapes the workspace's identity, not its content access.
    const seatLimit = parseSeatLimit();
    if (seatLimit === false) return;

    try {
      setSaving(true);

      // Upload files if selected
      let finalLogoUrl = logoUrl;
      if (selectedLogo) {
        const result = await uploadFile(selectedLogo);
        finalLogoUrl = result?.uploadedUrl || '';
      }

      let finalFavicon = favicon;
      if (selectedFavicon) {
        const result = await uploadFile(selectedFavicon);
        finalFavicon = result?.uploadedUrl || '';
      }

      let finalBannerImageUrl = bannerImageUrl;
      if (selectedBannerImage) {
        const result = await uploadFile(selectedBannerImage);
        finalBannerImageUrl = result?.uploadedUrl || '';
      }

      // Serialize the multi-widget editor into the single backend
      // string field. Empty list → undefined so the column clears.
      const serializedWidgets = serializeWidgets(dashboardWidgets);
      const finalDashboardWidgetHtml = serializedWidgets || undefined;

      await workspacesClient.updateWorkspace(Number(id), {
        name,
        primaryColor: primaryColor || undefined,
        logoUrl: finalLogoUrl || undefined,
        favicon: finalFavicon || undefined,
        // Banner is now image-only — no rich text. We still write
        // bannerHtml so an existing tenant value gets cleared if the
        // admin explicitly wiped it elsewhere (it's effectively
        // always empty from this UI now).
        bannerHtml: bannerHtml || undefined,
        bannerImageUrl: finalBannerImageUrl || undefined,
        dashboardWidgetHtml: finalDashboardWidgetHtml,
      });

      // Persist the access bundle (seat limit + tier + toggles + grants)
      // in the same save. Full-replace endpoint; its return is the
      // authoritative workspace (core fields just written above + the
      // enriched grant collections), so adopt it as local state.
      const updatedWorkspace = await workspacesClient.updateWorkspaceAccess(
        Number(id),
        buildAccessRequest(seatLimit),
      );

      // Update local state with final values
      setWorkspace(updatedWorkspace);
      setLogoUrl(finalLogoUrl);
      setFavicon(finalFavicon);
      setBannerImageUrl(finalBannerImageUrl);
      setBannerImageUrlDraft(finalBannerImageUrl);
      setDashboardWidgetHtml(serializedWidgets);
      setSelectedLogo(null);
      setSelectedFavicon(null);
      setSelectedBannerImage(null);

      // If this workspace is currently selected in the switcher, apply branding live
      if (selectedWorkspace?.id === Number(id)) {
        let liveLogoUrl = account.logoUrl;
        if (finalLogoUrl) {
          try {
            liveLogoUrl = await getFileUrl(finalLogoUrl);
          } catch {
            liveLogoUrl = finalLogoUrl;
          }
        }
        dispatch(
          setAccount({
            accountId: account.accountId,
            name: name || account.name,
            logoUrl: liveLogoUrl,
            primaryColor: primaryColor || account.primaryColor,
            domain: account.domain,
            bannerHtml: bannerHtml || null,
            bannerImageUrl: finalBannerImageUrl || null,
            dashboardWidgetHtml: dashboardWidgetHtml || null,
          })
        );
      }

      alert(enqueueSnackbar, 'Workspace updated successfully', 'success');
    } catch (error: any) {
      const errorMessage = error?.response?.data;
      alert(enqueueSnackbar, typeof errorMessage === 'string' ? errorMessage : 'Failed to update workspace', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Parses + validates the seat-limit input. Returns the integer (or null
  // for unlimited), or `false` when the input is invalid so callers can
  // bail. Shared by the General-tab save and the Access-tab save since
  // both send a seat limit.
  const parseSeatLimit = (): number | null | false => {
    const trimmed = seatLimitInput.trim();
    if (!trimmed) return null;
    const parsed = parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      alert(enqueueSnackbar, 'Seat limit must be a non-negative number, or empty for unlimited.', 'error');
      return false;
    }
    return parsed;
  };

  // Assemble the FULL access bundle from current tab state. The
  // `updateWorkspaceAccess` endpoint is a full replace, so every save —
  // whether from the General tab (scalars) or the Access tab (grants) —
  // sends the complete payload to avoid one tab clobbering the other's
  // fields. Both tabs' fields live in component state simultaneously, so
  // this is always the live, complete picture.
  const buildAccessRequest = (seatLimit: number | null): UpdateWorkspaceAccessRequest => ({
    seatLimit: seatLimit ?? undefined,
    tier: accountTypeToTier(accountType),
    allowLearnerSelfEdit,
    emailNotificationsEnabled,
    contentTagIds: contentTags.map((t) => t.id!).filter(Boolean),
    provisionedCourseIds: provisionedContent.map((c) => c.id!).filter(Boolean),
  });

  const handleAccessSave = async () => {
    if (!id) return;
    const seatLimit = parseSeatLimit();
    if (seatLimit === false) return;
    setAccessSaving(true);
    try {
      const updated = await workspacesClient.updateWorkspaceAccess(
        Number(id),
        buildAccessRequest(seatLimit),
      );
      setWorkspace(updated);
      alert(enqueueSnackbar, 'Access settings saved', 'success');
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to save access settings', 'error');
    } finally {
      setAccessSaving(false);
    }
  };


  // Toggle workspace activation through the workspaces API. The server
  // stamps/clears `deactivatedAt`; we refresh the local flag and the
  // loaded workspace from the response so the UI (and the delete gate)
  // reflect the new state immediately. The Users page reads the same
  // server state on its next load, so every workspace member shows
  // "Inactive" — the cascade the user expects.
  const handleToggleDeactivated = async () => {
    if (!workspace?.id) return;
    const next = !deactivated;
    setTogglingStatus(true);
    try {
      const updated = await workspacesClient.setWorkspaceStatus(workspace.id, {
        deactivated: next,
      });
      setWorkspace(updated);
      setDeactivated(updated.deactivatedAt != null);
      alert(
        enqueueSnackbar,
        next
          ? 'Workspace deactivated. Members render as inactive across the admin.'
          : 'Workspace reactivated.',
        'success',
      );
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to update workspace status', 'error');
    } finally {
      setTogglingStatus(false);
    }
  };

  // Delete is gated on deactivation in the UI (button disabled with
  // tooltip until the workspace is deactivated). The confirmation dialog
  // is a second gate on top of that; the server also enforces
  // deactivate-before-delete. On success, bounce to the workspaces list.
  const handleConfirmDelete = async () => {
    if (!workspace?.id) return;
    setDeleting(true);
    try {
      await workspacesClient.deleteWorkspace(workspace.id);
      alert(enqueueSnackbar, 'Workspace deleted', 'success');
      navigate('/workspaces');
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to delete workspace', 'error');
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  const handleViewAsWorkspaceAdmin = () => {
    if (!workspace?.id) return;
    impersonateWorkspaceAdmin(workspace.id, `/workspaces/${workspace.id}`).catch((err) =>
      alert(enqueueSnackbar, err?.message || 'Failed to impersonate', 'error'),
    );
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, setFile: (f: File | null) => void, setPreview: (url: string | null) => void) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <PageHeader
        icon={<WorkspacesIcon />}
        title={workspace?.name || 'Workspace'}
        actions={
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={handleViewAsWorkspaceAdmin}
            sx={{
              textTransform: 'none',
              borderColor: 'rgba(0,0,0,0.23)',
              color: 'text.primary',
              '&:hover': { borderColor: 'rgba(0,0,0,0.5)', bgcolor: 'action.hover' },
            }}
          >
            View as Workspace Admin
          </Button>
        }
      />

      {/* Separator between the workspace title and the tab strip — a
          single hairline visually anchors the title block so the tabs
          read as the start of the page body rather than floating
          immediately under the title. */}
      <Divider sx={{ mb: 2 }} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(_e, value) => setSelectedTab(value)}>
          <Tab label="General" value="general" />
          <Tab label="Access" value="access" icon={<LibraryBooksIcon />} iconPosition="start" />
          <Tab label="Groups" value="groups" icon={<GroupIcon />} iconPosition="start" />
          <Tab label="Users" value="users" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Assignments" value="assignments" icon={<BookmarkIcon />} iconPosition="start" />
          <Tab label="Dashboard" value="dashboard" icon={<DashboardIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* General Tab — card-based panels. Each card groups related
          settings under one accent-railed header so the page reads as
          a sequence of distinct concerns (Workspace / Branding /
          Permissions) instead of a long undifferentiated form. Mirrors
          the visual treatment used on the Access tab. */}
      {selectedTab === 'general' && (
        <Box sx={{ maxWidth: 920, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <WorkspaceGeneralTab
            name={name}
            setName={setName}
            accountType={accountType}
            setAccountType={setAccountType}
            seatLimitInput={seatLimitInput}
            setSeatLimitInput={setSeatLimitInput}
            primaryColor={primaryColor}
            setPrimaryColor={setPrimaryColor}
            logoPreview={logoPreview}
            onLogoFileSelect={(e) => handleFileSelect(e, setSelectedLogo, setLogoPreview)}
            onLogoRemove={() => {
              setSelectedLogo(null);
              setLogoPreview(null);
              setLogoUrl('');
            }}
            faviconPreview={faviconPreview}
            onFaviconFileSelect={(e) => handleFileSelect(e, setSelectedFavicon, setFaviconPreview)}
            onFaviconRemove={() => {
              setSelectedFavicon(null);
              setFaviconPreview(null);
              setFavicon('');
            }}
            allowLearnerSelfEdit={allowLearnerSelfEdit}
            setAllowLearnerSelfEdit={setAllowLearnerSelfEdit}
            emailNotificationsEnabled={emailNotificationsEnabled}
            setEmailNotificationsEnabled={setEmailNotificationsEnabled}
          />
          <WorkspaceDangerZone
            workspaceName={workspace?.name ?? ''}
            deactivated={deactivated}
            togglingStatus={togglingStatus}
            onToggleDeactivated={handleToggleDeactivated}
            confirmDeleteOpen={confirmDeleteOpen}
            setConfirmDeleteOpen={setConfirmDeleteOpen}
            deleting={deleting}
            onConfirmDelete={handleConfirmDelete}
          />
        </Box>
      )}

      {/* Access Tab — same card pattern as General so the two tabs
          read with one visual language. Width matches General too. */}
      {selectedTab === 'access' && (
        <WorkspaceAccessTab
          contentTags={contentTags}
          setContentTags={setContentTags}
          provisionedContent={provisionedContent}
          setProvisionedContent={setProvisionedContent}
        />
      )}

      {/* Groups Tab — list workspace groups. Creating a group opens a
          dialog (see below) where all the initial settings live — name,
          description, brand. Access rules + members are configured on
          the group's edit page after the row exists. */}
      {selectedTab === 'groups' && workspace?.id && (
        <GroupsDirectory workspaceIdLock={workspace.id} workspaceName={workspace.name ?? ''} />
      )}

      {/* Users Tab — drilled-in view of the workspace's roster. Mounts
          the shared UsersDirectory with `workspaceIdLock` set, which
          hides the workspace dropdown + column and forces every query
          to this tenant. Same filters, bulk actions, row menu, and
          dialogs as the standalone /students page — just scoped. */}
      {selectedTab === 'users' && workspace?.id && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Header matches the Groups tab — title + accent count, no
              subtitle. The search field + Invite CTA live INSIDE
              UsersDirectory's toolbar; pulling them up here would
              fragment the directory's filter state, so the chrome
              stays minimal at this level. */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Users
            </Typography>
            {usersTotal > 0 && (
              <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 600 }}>
                ({usersTotal})
              </Typography>
            )}
          </Box>
          <UsersDirectory
            workspaceIdLock={workspace.id}
            onTotalCountChange={setUsersTotal}
          />
        </Box>
      )}

      {/* Assignments Tab — drilled-in view scoped to this workspace.
          Mounts the shared AssignmentsDirectory with `workspaceIdLock`
          set; the directory renders its own inline Add button on the
          Setup sub-tab so admins don't need a separate page-header
          CTA. Same sub-tabs (Setup + Assignments), search, and tables
          as the standalone /assignments page — just scoped. */}
      {selectedTab === 'assignments' && workspace?.id && (
        <Box>
          <AssignmentsDirectory workspaceIdLock={workspace.id} />
        </Box>
      )}

      {/* Dashboard Tab */}
      {selectedTab === 'dashboard' && (
        <WorkspaceDashboardTab
          bannerImagePreview={bannerImagePreview}
          bannerImageUrlDraft={bannerImageUrlDraft}
          setBannerImageUrlDraft={setBannerImageUrlDraft}
          onBannerFileSelect={(e) => handleFileSelect(e, setSelectedBannerImage, setBannerImagePreview)}
          onBannerUrlBlur={() => {
            // Promote draft → saved value on blur so the preview updates
            // as soon as the admin tabs away. Empty draft doesn't clobber
            // an existing upload — admins use Remove for that. A pasted
            // URL replaces a previously-selected file (same field, WYSIWYG).
            const next = bannerImageUrlDraft.trim();
            if (!next) return;
            setBannerImageUrl(next);
            setBannerImagePreview(next);
            setSelectedBannerImage(null);
          }}
          onBannerRemove={() => {
            setSelectedBannerImage(null);
            setBannerImagePreview(null);
            setBannerImageUrl('');
            setBannerImageUrlDraft('');
          }}
          dashboardWidgets={dashboardWidgets}
          setDashboardWidgets={setDashboardWidgets}
          widgetEditStates={widgetEditStates}
          setWidgetEditStates={setWidgetEditStates}
        />
      )}

      {/* Sticky save bar — one bar for the whole page, swaps handler
          + label by tab. Pinned to the bottom of MainLayout's scroll
          container so admins on the long General / Access / Dashboard
          tabs don't have to scroll to the end to commit changes.
          Hidden on tabs that have no save (Users / Assignments /
          Groups — those manage row data through their own dialogs).

          Negative horizontal margin (`mx: -4`) cancels the outer page
          padding so the bar's border-top spans the full content width;
          inner `px: 4` restores the content inset on the right where
          the button sits. */}
      {(selectedTab === 'general' ||
        selectedTab === 'access' ||
        selectedTab === 'dashboard') && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            mx: -4,
            mt: 3,
            px: 4,
            py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(6px)',
            borderTop: '1px solid',
            borderColor: 'divider',
            // Above the danger-zone card and most content; below
            // dialogs (which start at zIndex.modal in MUI).
            zIndex: 5,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={selectedTab === 'access' ? handleAccessSave : handleSave}
            disabled={selectedTab === 'access' ? accessSaving : saving}
            sx={{
              px: 3,
              // Hold the gradient + white text through the disabled /
              // saving state — the wrapper's auto-gradient drops both
              // under MUI's default disabled treatment otherwise.
              '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
            }}
          >
            {(selectedTab === 'access' ? accessSaving : saving) ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Save changes'
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
}
